import React, { useState, useEffect } from "react";
import useSWR from 'swr';
import { toast } from 'sonner';
import api from "../utils/api";
import { Bell, BellRing, Calendar, User, Package, Zap, DollarSign, UserCheck, FileSignature, XCircle, RotateCcw, MessageSquare, Ship, AlertCircle, CreditCard, Smartphone } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { isPushSupported, subscribeToPush, syncPushSubscription } from "../utils/webPush";

const NotificationBell = ({ onViewRequests }) => {
  const [open, setOpen] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [pushGranted, setPushGranted] = useState(
    typeof Notification !== 'undefined' ? Notification.permission === 'granted' : false
  );

  useEffect(() => {
    api.get('/v2/notification-preferences')
      .then((res) => {
        setNotifPrefs(res.data);
        if (res.data?.hasTeamProfile) {
          if (res.data.push_enabled !== false) {
            syncPushSubscription();
          }
          maybePromptFirstTime(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const maybePromptFirstTime = (prefs) => {
    if (!isPushSupported()) return;
    if (Notification.permission !== 'default') return;
    if (prefs.push_enabled === false) return;
    if (localStorage.getItem('pushPromptShown')) return;
    localStorage.setItem('pushPromptShown', '1');
    toast("Get notified when you're assigned to a booking", {
      description: 'Enable push notifications so you never miss an assignment.',
      duration: 12000,
      action: { label: 'Enable', onClick: () => handlePushToggle(true) },
    });
  };

  const handlePushToggle = async (checked) => {
    if (checked) {
      const result = await subscribeToPush();
      if (result.ok) {
        setPushGranted(true);
        api.patch('/v2/notification-preferences', { push_enabled: true }).catch(() => {});
        setNotifPrefs((p) => ({ ...p, push_enabled: true }));
        toast.success('Push notifications enabled');
      } else if (result.reason === 'denied') {
        toast.error('Notifications are blocked. Allow them in your browser settings to enable push.');
      } else if (result.reason === 'unsupported') {
        toast.error('Push notifications are not supported on this device or browser.');
      } else {
        toast.error('Could not enable push notifications. Please try again.');
      }
    } else {
      api.patch('/v2/notification-preferences', { push_enabled: false }).catch(() => {});
      setNotifPrefs((p) => ({ ...p, push_enabled: false }));
      toast.success('Push notifications turned off');
    }
  };

  const handleSmsToggle = (checked) => {
    api.patch('/v2/notification-preferences', { sms_enabled: checked }).catch(() => {});
    setNotifPrefs((p) => ({ ...p, sms_enabled: checked }));
    toast.success(checked ? 'SMS notifications enabled' : 'SMS notifications turned off');
  };

  const [viewedActivities, setViewedActivities] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('viewedNotifications');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (e) {
        return new Set();
      }
    }
    return new Set();
  });
  
  // SWR fetcher
  const fetcher = (url) => api.get(url).then(res => res.data);
  
  // Team members only see their own notifications (own actions + bookings
  // assigned to them); owners/admins see the full org feed.
  const isTeamMember = (localStorage.getItem('userType') || '').toLowerCase() === 'teammember';
  const activityLogUrl = isTeamMember ? '/activity-log?limit=20&scope=mine' : '/activity-log?limit=20';

  // Fetch recent activities with SWR (auto-refresh every 60s)
  const { data, error, isLoading } = useSWR(
    activityLogUrl,
    fetcher,
    {
      refreshInterval: 60000, // Auto-refresh every 60 seconds (was 30s)
      revalidateOnFocus: true, // Refresh when tab becomes active
      dedupingInterval: 10000 // Prevent duplicate requests within 10s
    }
  );
  
  const allActivities = data?.activities || [];

  // Normalize verb tenses so the two backend loggers (lowercase `create` and
  // uppercase `CREATE`) collapse to a single key.
  const normalizeAction = (a) => {
    const x = (a || '').toLowerCase();
    const map = {
      create: 'created', update: 'updated', cancel: 'cancelled',
      check_in: 'checked_in', refund: 'refunded',
    };
    return map[x] || x;
  };

  // Filter to notification-worthy events
  const notificationEvents = allActivities.filter(activity => {
    const action = activity.action?.toLowerCase() || '';
    const resourceType = activity.resourceType?.toLowerCase() || '';
    
    // Notification-worthy events - comprehensive list
    // Note: Actions can be 'created' or 'CREATE' depending on source
    return (
      // Bookings
      (action === 'created' && resourceType === 'booking') ||
      (action === 'create' && resourceType === 'booking') ||
      (action === 'imported' && resourceType === 'booking') ||
      (action === 'checkout_reached' && resourceType === 'booking') ||
      (action === 'cancel_requested' && resourceType === 'booking') ||
      (action === 'change_requested' && resourceType === 'booking') ||
      (action === 'confirmed' && resourceType === 'booking') ||
      (action === 'cancelled' && resourceType === 'booking') ||
      (action === 'cancel' && resourceType === 'booking') ||
      (action === 'rescheduled' && resourceType === 'booking') ||
      (action === 'check_in' && resourceType === 'booking') ||
      (action === 'checked_in' && resourceType === 'booking') ||
      
      // Payments
      (action === 'payment_received' && resourceType === 'booking') ||
      (action === 'refunded' && resourceType === 'booking') ||
      (action === 'partially_refunded' && resourceType === 'booking') ||
      
      // Leads & Custom Requests
      (action === 'captured' && resourceType === 'lead') ||
      (action === 'created' && resourceType === 'lead') ||
      (action === 'create' && resourceType === 'lead') ||
      (action === 'created' && resourceType === 'custom_request') ||
      (action === 'create' && resourceType === 'custom_request') ||
      (action === 'submitted' && resourceType === 'custom_request') ||
      
      // Check-ins & Waivers
      (action === 'signed' && resourceType === 'waiver') ||
      
      // Services
      (action === 'created' && resourceType === 'service') ||
      (action === 'create' && resourceType === 'service')
    );
  });

  // De-duplicate: the backend has two loggers that each record the same event
  // (lowercase `create/booking` AND uppercase `CREATE/Booking`), which used to
  // surface as phantom duplicate alerts. Collapse by resourceId + normalized
  // action, keeping the most recent. Entries without a resourceId are kept as-is.
  const dedupedEvents = (() => {
    const seen = new Map();
    const out = [];
    for (const a of notificationEvents) {
      const rid = a.resourceId;
      if (!rid) { out.push(a); continue; }
      const key = `${(a.resourceType || '').toLowerCase()}:${rid}:${normalizeAction(a.action)}`;
      if (seen.has(key)) continue; // list is already newest-first → first wins
      seen.set(key, true);
      out.push(a);
    }
    return out;
  })();
  
  // Show max 10 in dropdown
  const recentNotifications = dedupedEvents.slice(0, 10);
  
  // Count only UNVIEWED
  const unviewedCount = dedupedEvents.filter(activity => {
    const id = activity.id || activity.timestamp;
    return id && !viewedActivities.has(id);
  }).length;
  
  // Cap badge at 9+
  const badgeCount = unviewedCount > 9 ? '9+' : unviewedCount;
  
  // Mark all as viewed when opened
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    
    if (isOpen && recentNotifications.length > 0) {
      // Mark all current notifications as viewed
      const currentIds = recentNotifications.map(a => a.id || a.timestamp).filter(Boolean);
      const newViewed = new Set([...viewedActivities, ...currentIds]);
      setViewedActivities(newViewed);
      localStorage.setItem('viewedNotifications', JSON.stringify([...newViewed]));
    }
  };

  const getActivityIcon = (activity) => {
    const resource = activity.resourceType?.toLowerCase() || '';
    const action = activity.action?.toLowerCase() || '';
    
    // GetMyBoat import
    if (action === 'imported') return Ship;
    if (action === 'checkout_reached') return CreditCard;
    if (action === 'cancel_requested') return XCircle;
    if (action === 'change_requested') return AlertCircle;
    
    // Payment/refund icons
    if (action === 'payment_received') return DollarSign;
    if (action === 'refunded' || action === 'partially_refunded') return RotateCcw;
    
    // Cancellation
    if (action === 'cancelled') return XCircle;
    
    // Custom Requests (high priority!)
    if (resource === 'custom_request') return MessageSquare;
    
    // Leads
    if (action === 'captured' && resource === 'lead') return UserCheck;
    if (action === 'created' && resource === 'lead') return UserCheck;
    
    // Check-in
    if (action === 'checked_in') return UserCheck;
    
    // Waiver
    if (action === 'signed' && resource === 'waiver') return FileSignature;
    
    // Bookings
    if (resource.includes('booking')) return Calendar;
    
    // Services
    if (resource.includes('service')) return Zap;
    if (resource.includes('asset')) return Package;
    if (resource.includes('team')) return User;
    
    return Calendar;
  };
  
  const getActivityColor = (activity) => {
    const action = activity.action?.toLowerCase() || '';
    const resource = activity.resourceType?.toLowerCase() || '';
    
    // GetMyBoat import = cyan/teal (brand-adjacent, distinct from new BF bookings)
    if (action === 'imported') {
      return 'text-cyan-600';
    }
    if (action === 'checkout_reached') return 'text-indigo-600';
    if (action === 'cancel_requested') return 'text-rose-600';
    if (action === 'change_requested') return 'text-amber-600';
    // Refunds/cancellations = red
    if (action === 'cancelled' || action === 'refunded' || action === 'partially_refunded') {
      return 'text-red-600';
    }
    // Payments = green
    if (action === 'payment_received') {
      return 'text-green-600';
    }
    // Custom requests = purple (high priority!)
    if (resource === 'custom_request') {
      return 'text-purple-600';
    }
    // Leads = orange
    if (action === 'captured' || (action === 'created' && activity.resourceType?.toLowerCase() === 'lead')) {
      return 'text-orange-600';
    }
    // Default = blue
    return 'text-blue-600';
  };

  const getActivityMessage = (activity) => {
    const action = activity.action?.toLowerCase() || '';
    const resource = activity.resourceType?.toLowerCase() || '';
    const name = activity.resourceName || activity.description?.substring(0, 30) || 'Item';
    
    // Custom messages for each event type
    if (action === 'imported' && resource === 'booking') {
      // gmb_sync writes a friendly description, e.g. "Imported 3 GetMyBoat bookings"
      return activity.description || 'New GetMyBoat booking';
    }
    if (action === 'checkout_reached' && resource === 'booking') {
      const d = activity.details || {};
      if (d.customerName && d.serviceName) return `Reached checkout: ${d.customerName} · ${d.serviceName} → Stripe`;
      return activity.description || 'Reached checkout — redirected to Stripe';
    }
    if ((action === 'cancel_requested' || action === 'change_requested') && resource === 'booking') {
      return activity.description || (action === 'cancel_requested'
        ? 'Booking cancelled on GetMyBoat' : 'Change requested on GetMyBoat');
    }
    if ((action === 'created' || action === 'create') && resource === 'booking') {
      // Prefer the real customer + service (logged in extra_data) so the bell is
      // specific. Old/generic entries (e.g. "Created a booking") fall back cleanly.
      const d = activity.details || {};
      if (d.customerName && d.serviceName) return `New booking: ${d.customerName} · ${d.serviceName}`;
      if (d.customerName) return `New booking: ${d.customerName}`;
      return 'New booking';
    }
    if (action === 'confirmed' && resource === 'booking') {
      return `Booking confirmed: ${name}`;
    }
    if ((action === 'cancelled' || action === 'cancel') && resource === 'booking') {
      return `Booking cancelled: ${name}`;
    }
    if (action === 'rescheduled' && resource === 'booking') {
      return `Booking rescheduled: ${name}`;
    }
    if (action === 'payment_received') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Payment received ${amount} - ${name}`;
    }
    if (action === 'refunded') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Refund issued ${amount} - ${name}`;
    }
    if (action === 'partially_refunded') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Partial refund ${amount} - ${name}`;
    }
    // Custom Requests (high priority!)
    if (resource === 'custom_request') {
      return `📩 Custom request: ${name}`;
    }
    if ((action === 'captured' || action === 'created' || action === 'create') && resource === 'lead') {
      return `New lead: ${name}`;
    }
    if (action === 'checked_in' || action === 'check_in') {
      return `${name} checked in`;
    }
    if (action === 'signed' && resource === 'waiver') {
      return `Waiver signed by ${name}`;
    }
    if ((action === 'created' || action === 'create') && resource === 'service') {
      return `New service: ${name}`;
    }
    
    // Fallback
    return `${action} ${resource}: ${name}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unviewedCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {badgeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-1.5rem)] max-w-96 sm:w-96" align="end" collisionPadding={12}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            {unviewedCount > 0 && (
              <Badge variant="secondary">{unviewedCount} new</Badge>
            )}
          </div>
          
          {recentNotifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No recent notifications
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recentNotifications.map((activity, index) => {
                const ResourceIcon = getActivityIcon(activity);
                const iconColor = getActivityColor(activity);
                
                return (
                  <div 
                    key={activity.id || index} 
                    className="p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <ResourceIcon className={`w-4 h-4 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {getActivityMessage(activity)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestamp(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {notifPrefs?.hasTeamProfile && (
            <div className="pt-3 border-t space-y-3" data-testid="notification-settings">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                My Notifications
              </p>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <BellRing className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Push notifications</p>
                    <p className="text-xs text-muted-foreground truncate">Assignment alerts on this device</p>
                  </div>
                </div>
                <Switch
                  data-testid="push-notifications-toggle"
                  checked={notifPrefs.push_enabled !== false && pushGranted}
                  onCheckedChange={handlePushToggle}
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Smartphone className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">SMS notifications</p>
                    <p className="text-xs text-muted-foreground truncate">Text alerts for assignments &amp; changes</p>
                  </div>
                </div>
                <Switch
                  data-testid="sms-notifications-toggle"
                  checked={notifPrefs.sms_enabled !== false}
                  onCheckedChange={handleSmsToggle}
                />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
